import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, ChevronRight, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { brandLink } from "../../components/PublicLayout";
import { useBrand } from "../../contexts/BrandContext";
import { getMountBrand } from "../../lib/mounts";
import SEO from "../../components/SEO";

export default function ProductDetail() {
  const { slug } = useParams();
  const { mode, brand } = useBrand();
  const [product, setProduct] = useState(null);
  const [category, setCategory] = useState(null);
  const [productBrand, setProductBrand] = useState(null);
  const [related, setRelated] = useState([]);
  const [activeImg, setActiveImg] = useState(0);
  const [selectedMount, setSelectedMount] = useState("");
  const [zoom, setZoom] = useState({ active: false, x: 50, y: 50 });
  const [notFound, setNotFound] = useState(false);
  const [showInquiry, setShowInquiry] = useState(false);
  const imgRef = useRef(null);

  useEffect(() => {
    setProduct(null);
    setActiveImg(0);
    setSelectedMount("");
    api
      .get(`/products/${slug}`)
      .then((r) => setProduct(r.data))
      .catch(() => setNotFound(true));
  }, [slug]);

  useEffect(() => {
    if (!product) return;
    if (product.mounts?.length === 1) setSelectedMount(product.mounts[0]);

    const fetchAux = async () => {
      const [cats, brands] = await Promise.all([
        api.get(`/categories`).then((r) => r.data).catch(() => []),
        api.get(`/brands`).then((r) => r.data).catch(() => []),
      ]);
      const cat = cats.find((c) => c.id === product.category_id);
      const br = brands.find((b) => b.id === product.brand_id);
      setCategory(cat || null);
      setProductBrand(br || null);

      // related products: same brand, prefer same category, exclude current
      const params = new URLSearchParams();
      params.set("brand_id", product.brand_id);
      if (product.category_id) params.set("category_id", product.category_id);
      let rel = await api.get(`/products?${params.toString()}`).then((r) => r.data).catch(() => []);
      rel = rel.filter((p) => p.id !== product.id);
      if (rel.length < 4 && product.category_id) {
        const more = await api
          .get(`/products?brand_id=${product.brand_id}`)
          .then((r) => r.data)
          .catch(() => []);
        for (const m of more) {
          if (rel.length >= 8) break;
          if (m.id !== product.id && !rel.find((x) => x.id === m.id)) rel.push(m);
        }
      }
      setRelated(rel.slice(0, 4));
    };
    fetchAux();
  }, [product]);

  const onMouseMove = (e) => {
    const r = imgRef.current?.getBoundingClientRect();
    if (!r) return;
    const x = ((e.clientX - r.left) / r.width) * 100;
    const y = ((e.clientY - r.top) / r.height) * 100;
    setZoom({ active: true, x, y });
  };

  const inquiryBrandName = useMemo(
    () => productBrand?.name || (mode === "brand" ? brand?.name : ""),
    [productBrand, mode, brand]
  );

  if (notFound) {
    return (
      <div className="max-w-7xl mx-auto px-6 py-20 text-center text-zinc-400" data-testid="product-not-found">
        <SEO title="Ürün Bulunamadı" />
        Ürün bulunamadı.
      </div>
    );
  }
  if (!product) return null;

  const statusLabel = {
    in_stock: { label: "Stokta", color: "text-emerald-400 border-emerald-400/40" },
    out_of_stock: { label: "Tükendi", color: "text-red-400 border-red-400/40" },
    coming_soon: { label: "Çok Yakında", color: "text-amber-400 border-amber-400/40" },
  }[product.stock_status] || { label: "—", color: "text-zinc-400 border-white/20" };

  return (
    <div className="fade-in-up" data-testid="product-detail-page">
      <SEO title={`${product.name}${inquiryBrandName ? ` | ${inquiryBrandName}` : ""}`} override description={product.description?.slice(0, 160)} />

      {/* Breadcrumb */}
      <div className="border-b border-white/10 bg-[#080808]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <nav className="flex items-center gap-2 text-xs text-zinc-500 overflow-x-auto" data-testid="breadcrumb">
            <Link to={brandLink("/")} className="hover:text-white whitespace-nowrap">Ana Sayfa</Link>
            <ChevronRight size={12} />
            <Link to={brandLink("/urunler")} className="hover:text-white whitespace-nowrap" data-testid="breadcrumb-products">Ürünler</Link>
            {category && (
              <>
                <ChevronRight size={12} />
                <Link to={brandLink(`/urunler?category=${category.id}`)} className="hover:text-white whitespace-nowrap" data-testid="breadcrumb-category">
                  {category.name}
                </Link>
              </>
            )}
            <ChevronRight size={12} />
            <span className="text-zinc-300 truncate" data-testid="breadcrumb-current">{product.name}</span>
          </nav>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <Link
          to={brandLink("/urunler")}
          className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-zinc-400 hover:text-white mb-8"
          data-testid="back-to-products"
        >
          <ArrowLeft size={14} /> Ürünlere Dön
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-10">
          {/* Image gallery — takes more horizontal space */}
          <div className="lg:col-span-3">
            <div
              ref={imgRef}
              className="aspect-square bg-[#0A0A0A] border border-white/10 p-8 lg:p-14 flex items-center justify-center overflow-hidden relative cursor-zoom-in"
              onMouseEnter={() => setZoom((z) => ({ ...z, active: true }))}
              onMouseLeave={() => setZoom({ active: false, x: 50, y: 50 })}
              onMouseMove={onMouseMove}
              data-testid="product-main-image"
            >
              {product.images?.[activeImg] ? (
                <img
                  src={product.images[activeImg]}
                  alt={product.name}
                  className="max-h-full max-w-full object-contain transition-transform duration-200 ease-out will-change-transform"
                  style={{
                    transform: zoom.active ? "scale(2)" : "scale(1)",
                    transformOrigin: `${zoom.x}% ${zoom.y}%`,
                  }}
                />
              ) : (
                <div className="text-zinc-700 text-xs uppercase tracking-[0.2em]">No Image</div>
              )}
            </div>
            {product.images?.length > 1 && (
              <div className="grid grid-cols-6 gap-2 mt-3" data-testid="product-thumbnails">
                {product.images.map((img, idx) => (
                  <button
                    key={idx}
                    onClick={() => setActiveImg(idx)}
                    className={`aspect-square border bg-[#0A0A0A] p-2 transition-colors ${
                      activeImg === idx ? "border-white/40" : "border-white/10 hover:border-white/20"
                    }`}
                    data-testid={`thumb-${idx}`}
                  >
                    <img src={img} alt="" className="w-full h-full object-contain" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Info column — sticky on desktop so CTA stays in view while scrolling specs */}
          <div className="lg:col-span-2">
            <div className="lg:sticky lg:top-24" data-testid="product-info-panel">
              {productBrand && mode === "main" && (
                <Link
                  to={`/?brand=${productBrand.slug}`}
                  className="inline-block text-[10px] uppercase tracking-[0.3em] text-zinc-500 hover:text-white mb-3"
                  data-testid="product-brand-link"
                >
                  {productBrand.name}
                </Link>
              )}
              <div
                className={`inline-block text-[10px] uppercase tracking-[0.3em] px-3 py-1 border ${statusLabel.color} mb-4`}
                data-testid="product-status"
              >
                {statusLabel.label}
              </div>
              <h1 className="font-display text-3xl sm:text-4xl tracking-tighter" data-testid="product-name">
                {product.name}
              </h1>

              {product.price != null && product.price > 0 && (
                <div className="mt-4 font-display text-2xl" style={{ color: "var(--brand-primary)" }} data-testid="product-price">
                  {Number(product.price).toLocaleString("tr-TR")} ₺
                </div>
              )}

              {/* Mount Selector */}
              {product.mounts?.length > 0 && (
                <div className="mt-6" data-testid="mount-selector">
                  <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-3">Mount Seçimi</div>
                  <div className="flex flex-wrap gap-2">
                    {product.mounts.map((m) => {
                      const meta = getMountBrand(m);
                      const active = selectedMount === m;
                      return (
                        <button
                          key={m}
                          type="button"
                          onClick={() => setSelectedMount(m)}
                          className="inline-flex items-center gap-2 px-3 py-2 text-xs border transition-all"
                          style={
                            active
                              ? {
                                  background: "color-mix(in srgb, var(--brand-primary) 15%, transparent)",
                                  borderColor: "var(--brand-primary)",
                                  color: "#fff",
                                }
                              : { borderColor: "rgba(255,255,255,0.1)", color: "#d4d4d8" }
                          }
                          data-testid={`mount-option-${m.replace(/\s+/g, "-").toLowerCase()}`}
                        >
                          {meta?.icon && (
                            <img
                              src={meta.icon}
                              alt={meta.brand}
                              className="h-4 w-4 object-contain"
                              style={{ filter: active ? "none" : "opacity(0.7)" }}
                            />
                          )}
                          <span>{m}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* CTA — sticky alongside info on desktop */}
              <div className="mt-6 flex flex-col gap-3" data-testid="product-cta-block">
                <button
                  type="button"
                  onClick={() => setShowInquiry(true)}
                  className="brand-btn px-6 py-3 text-sm uppercase tracking-[0.2em] inline-flex items-center justify-center"
                  data-testid="product-inquiry-btn"
                >
                  Sorgula
                </button>
                <Link
                  to={brandLink("/bayiler")}
                  className="px-6 py-3 text-sm uppercase tracking-[0.2em] border border-white/15 hover:bg-white/5 transition-colors text-center"
                  data-testid="product-find-dealer"
                >
                  Yetkili Bayi Bul
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Description */}
        {product.description && (
          <div className="mt-16 max-w-3xl" data-testid="product-description">
            <div className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-4">Açıklama</div>
            <div className="text-sm text-zinc-300 leading-relaxed whitespace-pre-wrap">{product.description}</div>
          </div>
        )}

        {/* Specs */}
        {Object.keys(product.specs || {}).length > 0 && (
          <div className="mt-16" data-testid="product-specs">
            <div className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-4">Teknik Özellikler</div>
            <div className="border border-white/10 max-w-3xl">
              {Object.entries(product.specs).map(([k, v], idx) => (
                <div
                  key={k}
                  className={`grid grid-cols-2 px-4 py-3 text-sm ${
                    idx % 2 === 0 ? "bg-[#0A0A0A]" : "bg-[#080808]"
                  }`}
                >
                  <div className="text-zinc-500 uppercase text-xs tracking-[0.15em]">{k}</div>
                  <div className="text-zinc-200">{v}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Related products */}
        {related.length > 0 && (
          <div className="mt-20" data-testid="related-products">
            <div className="flex items-end justify-between mb-6">
              <div>
                <div className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-2">İlgili Ürünler</div>
                <h2 className="font-display text-2xl tracking-tight">Benzer Ürünler</h2>
              </div>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
              {related.map((p) => (
                <Link
                  key={p.id}
                  to={brandLink(`/urunler/${p.slug}`)}
                  className="group block border border-white/10 bg-[#0A0A0A] hover:-translate-y-1 transition-all duration-300"
                  data-testid={`related-product-${p.slug}`}
                >
                  <div className="aspect-square overflow-hidden bg-[#141414] p-8">
                    {p.images?.[0] ? (
                      <img src={p.images[0]} alt={p.name} className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-500" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-zinc-700 text-xs">No Image</div>
                    )}
                  </div>
                  <div className="p-4 border-t border-white/10">
                    <div className="text-sm font-medium line-clamp-1">{p.name}</div>
                    <div className="text-xs text-zinc-500 mt-1 uppercase tracking-[0.15em]">
                      {p.stock_status === "in_stock" && "Stokta"}
                      {p.stock_status === "out_of_stock" && "Tükendi"}
                      {p.stock_status === "coming_soon" && "Çok Yakında"}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>

      {showInquiry && (
        <InquiryModal
          product={product}
          selectedMount={selectedMount}
          brandSlug={mode === "brand" ? brand?.slug : null}
          onClose={() => setShowInquiry(false)}
        />
      )}
    </div>
  );
}

function InquiryModal({ product, selectedMount, brandSlug, onClose }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/submissions", {
        type: "product_inquiry",
        ...form,
        brand_slug: brandSlug,
        product_id: product.id,
        product_name: product.name,
        selected_mount: selectedMount || null,
      });
      toast.success("Sorgunuz alındı. En kısa sürede dönüş yapacağız.");
      onClose();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="inquiry-modal"
    >
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md bg-[#0A0A0A] border border-white/10 p-6"
      >
        <div className="flex justify-between items-start mb-6">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-2">Ürün Sorgusu</div>
            <div className="font-display text-lg leading-tight">{product.name}</div>
            {selectedMount && (
              <div className="text-xs text-zinc-500 mt-1">
                Mount: <span className="text-zinc-300">{selectedMount}</span>
              </div>
            )}
          </div>
          <button type="button" onClick={onClose} className="text-zinc-400 hover:text-white" data-testid="inquiry-close">
            <X size={18} />
          </button>
        </div>

        <div className="space-y-3">
          <input
            required
            placeholder="Adınız Soyadınız"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="w-full bg-[#141414] border border-white/10 px-4 py-2.5 text-sm focus:outline-none focus:border-white/30"
            data-testid="inquiry-name"
          />
          <input
            required
            type="email"
            placeholder="E-posta"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            className="w-full bg-[#141414] border border-white/10 px-4 py-2.5 text-sm focus:outline-none focus:border-white/30"
            data-testid="inquiry-email"
          />
          <input
            placeholder="Telefon"
            value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value })}
            className="w-full bg-[#141414] border border-white/10 px-4 py-2.5 text-sm focus:outline-none focus:border-white/30"
            data-testid="inquiry-phone"
          />
          <textarea
            required
            placeholder="Mesajınız"
            rows={4}
            value={form.message}
            onChange={(e) => setForm({ ...form, message: e.target.value })}
            className="w-full bg-[#141414] border border-white/10 px-4 py-2.5 text-sm focus:outline-none focus:border-white/30"
            data-testid="inquiry-message"
          />
        </div>

        <button
          type="submit"
          disabled={submitting}
          className="brand-btn mt-5 w-full py-3 text-sm uppercase tracking-[0.2em] disabled:opacity-50"
          data-testid="inquiry-submit"
        >
          {submitting ? "Gönderiliyor..." : "Sorgu Gönder"}
        </button>
      </form>
    </div>
  );
}
